import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Car as CarIcon, Settings, Check, Clock, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCars } from '../context/CarContext';
import { Car } from '../types';
import CarCard from '../components/CarCard';
import AddCarForm from '../components/AddCarForm';
import EditCarForm from '../components/EditCarForm';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const { isAuthenticated } = useAuth();
  const { getAllCars, deleteCar, updateCar } = useCars();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  
  const cars = getAllCars();
  
  // Debug: log cars to see their structure
  console.log('🚗 Cars in AdminDashboard:', cars);
  cars.forEach(car => {
    console.log(`🔍 Car ${car.id}:`, {
      id: car.id,
      make: car.make,
      model: car.model,
      saleStatus: car.saleStatus,
      hasImages: car.images?.length > 0
    });
  });

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleDeleteCar = (carId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este vehículo?')) {
      deleteCar(carId);
      toast.success('Vehículo eliminado exitosamente');
    }
  };

  const handleEditCar = (car: Car) => {
    setSelectedCar(car);
    setShowEditForm(true);
  };

  const handleCloseEditForm = () => {
    setShowEditForm(false);
    setSelectedCar(null);
  };

  const handleStatusChange = async (carId: string, newStatus: 'available' | 'pending' | 'sold') => {
    console.log('🔄 handleStatusChange called:', { carId, newStatus });
    try {
      console.log('📞 Calling updateCar...');
      await updateCar(carId, { saleStatus: newStatus });
      console.log('✅ updateCar completed successfully');
      toast.success(`Estado cambiado a ${newStatus === 'available' ? 'Disponible' : newStatus === 'pending' ? 'Pendiente' : 'Vendido'}`);
    } catch (error) {
      console.error('❌ Error in handleStatusChange:', error);
      toast.error('Error al cambiar el estado del vehículo');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <CarIcon className="h-8 w-8 text-primary-600 mr-3" />
              Panel de Administración
            </h1>
            <p className="text-gray-600 mt-1">
              Gestiona tu inventario de vehículos
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to="/gcs-test"
              className="btn-secondary flex items-center space-x-2"
            >
              <Settings className="h-5 w-5" />
              <span>Test Google Cloud</span>
            </Link>
            <Link
              to="/firestore-test"
              className="btn-secondary flex items-center space-x-2"
            >
              <Settings className="h-5 w-5" />
              <span>Test Firestore</span>
            </Link>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Agregar Vehículo</span>
            </button>
          </div>
        </div>
      </div>

      {/* Debug Test Section */}
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
        <p><strong>Debug:</strong> Test de actualización de estado</p>
        {cars.length > 0 && (
          <button
            onClick={async () => {
              const testCar = cars[0];
              console.log('🧪 Testing update on first car:', testCar);
              try {
                await updateCar(testCar.id, { saleStatus: 'sold' });
                console.log('✅ Test update completed');
              } catch (error) {
                console.error('❌ Test update failed:', error);
              }
            }}
            className="mt-2 px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            Probar Actualización (Primer Carro → Vendido)
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg">
              <CarIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Vehículos</p>
              <p className="text-2xl font-bold text-gray-900">{cars.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CarIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Disponibles</p>
              <p className="text-2xl font-bold text-gray-900">
                {cars.filter(car => car.isAvailable).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <CarIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total</p>
              <p className="text-2xl font-bold text-gray-900">
                ${cars.reduce((sum, car) => sum + car.price, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Table/Grid */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Inventario de Vehículos</h2>
        </div>
        
        {cars.length > 0 ? (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map(car => (
                <div key={car.id} className="relative group">
                  <CarCard car={car} />
                  
                  {/* Admin Actions Overlay */}
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex flex-col space-y-2">
                      {/* Quick Status Change Buttons */}
                      <div className="flex space-x-1">
                        <button
                          onClick={() => {
                            console.log('🔘 Available button clicked for car:', car.id, 'current status:', car.saleStatus);
                            handleStatusChange(car.id, 'available');
                          }}
                          className={`p-1.5 rounded-full shadow-lg transition-colors ${
                            car.saleStatus === 'available' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-white hover:bg-green-50 text-green-600'
                          }`}
                          title="Marcar como disponible"
                        >
                          <Check className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => {
                            console.log('🟠 Pending button clicked for car:', car.id, 'current status:', car.saleStatus);
                            handleStatusChange(car.id, 'pending');
                          }}
                          className={`p-1.5 rounded-full shadow-lg transition-colors ${
                            car.saleStatus === 'pending' 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-white hover:bg-orange-50 text-orange-600'
                          }`}
                          title="Marcar como pendiente"
                        >
                          <Clock className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => {
                            console.log('🔴 Sold button clicked for car:', car.id, 'current status:', car.saleStatus);
                            handleStatusChange(car.id, 'sold');
                          }}
                          className={`p-1.5 rounded-full shadow-lg transition-colors ${
                            car.saleStatus === 'sold' 
                              ? 'bg-red-500 text-white' 
                              : 'bg-white hover:bg-red-50 text-red-600'
                          }`}
                          title="Marcar como vendido"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      
                      {/* Edit and Delete Buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditCar(car)}
                          className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                          title="Editar vehículo"
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteCar(car.id)}
                          className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                          title="Eliminar vehículo"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="p-12 text-center">
            <CarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay vehículos</h3>
            <p className="text-gray-600 mb-6">
              Comienza agregando tu primer vehículo al inventario.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary"
            >
              Agregar Primer Vehículo
            </button>
          </div>
        )}
      </div>

      {/* Add Car Form */}
      <AddCarForm 
        isOpen={showAddForm} 
        onClose={() => setShowAddForm(false)} 
      />

      {/* Edit Car Form */}
      {selectedCar && (
        <EditCarForm 
          isOpen={showEditForm} 
          onClose={handleCloseEditForm}
          car={selectedCar}
        />
      )}
    </div>
  );
};

export default AdminDashboard;